import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";

export default function AdminProfile() {
  const [form, setForm] = useState({
    name: "",
    tagline: "",
    bio: "",
    photo: "",
    quote: "",
    quote_author: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/author")
      .then((r) =>
        setForm({
          name: r.data.name || "",
          tagline: r.data.tagline || "",
          bio: r.data.bio || "",
          photo: r.data.photo || "",
          quote: r.data.quote || "",
          quote_author: r.data.quote_author || "",
        }),
      )
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/author", form);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20" data-testid="admin-profile-loading">
        <p className="text-ink-soft">Loading…</p>
      </div>
    );
  }

  return (
    <div className="bg-paper min-h-[80vh]" data-testid="admin-profile-page">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta mb-8"
          data-testid="admin-profile-back-link"
        >
          <ArrowLeft size={14} /> Dashboard
        </Link>

        <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-4">
          Author Profile
        </p>
        <h1 className="font-serif-display text-3xl lg:text-4xl text-ink tracking-tight">
          Your name & photo
        </h1>
        <p className="mt-3 text-sm text-ink-soft">
          This appears on the Home hero and the About page.
        </p>

        <div className="mt-12 space-y-8">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Name
            </label>
            <input
              value={form.name}
              onChange={set("name")}
              data-testid="admin-profile-name-input"
              className="w-full bg-transparent border-b border-rule focus:border-ink py-3 text-2xl font-serif-display text-ink outline-none"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Tagline (small kicker above your name)
            </label>
            <input
              value={form.tagline}
              onChange={set("tagline")}
              data-testid="admin-profile-tagline-input"
              className="w-full bg-transparent border-b border-rule focus:border-ink py-3 text-sm text-ink outline-none"
              placeholder="Author · Poet · Faith Writer"
            />
          </div>

          <ImageUploader
            value={form.photo}
            onChange={setVal("photo")}
            label="Author Photo"
            testIdPrefix="admin-profile-photo"
          />

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Bio (About page)
            </label>
            <textarea
              rows={9}
              value={form.bio}
              onChange={set("bio")}
              data-testid="admin-profile-bio-input"
              className="w-full bg-paper border border-rule px-4 py-3 text-base text-ink leading-relaxed outline-none focus:border-ink"
              placeholder="Write a paragraph or two…"
            />
            <p className="mt-2 text-xs text-ink-soft">
              Paragraphs separated by blank lines.
            </p>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Pull Quote (shown on About page & Home overlay)
            </label>
            <textarea
              rows={2}
              value={form.quote}
              onChange={set("quote")}
              data-testid="admin-profile-quote-input"
              className="w-full bg-paper border border-rule px-4 py-3 text-base text-ink leading-relaxed outline-none focus:border-ink font-serif-display italic"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Quote Attribution (who said it — e.g. Lucy, or leave blank to use your initials)
            </label>
            <input
              value={form.quote_author}
              onChange={set("quote_author")}
              data-testid="admin-profile-quote-author-input"
              className="w-full bg-transparent border-b border-rule focus:border-ink py-3 text-sm text-ink outline-none"
              placeholder="Lucy"
            />
          </div>

          <div className="pt-6 border-t border-rule">
            <button
              onClick={save}
              disabled={saving || !form.name}
              data-testid="admin-profile-save-btn"
              className="px-6 py-3 bg-terracotta text-paper text-xs tracking-[0.18em] uppercase hover:bg-ink transition-colors disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save Profile"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
